import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Store, ChevronRight, Check, X, Loader2, Eye, ExternalLink,
  User, Image as ImageIcon, MapPin, Share2, Settings,
  Phone, Mail, Globe, Clock, Award, Facebook, Instagram,
  Linkedin, Youtube, Palette, Upload, Trash2, Star,
  Building2, FileText, CheckCircle, AlertCircle, Shield, Link2, Scale, Package
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import ImageUploader from '../../../components/ImageUploader';

import { API_URL } from '../../../config/api';

// Labels pour les certifications
const certificationLabels = {
  rge: { label: 'RGE', description: 'Reconnu Garant de l\'Environnement', color: 'bg-green-100 text-green-700' },
  qualibat: { label: 'Qualibat', description: 'Qualification Bâtiment', color: 'bg-blue-100 text-blue-700' },
  qualipac: { label: 'QualiPAC', description: 'Pompe à Chaleur', color: 'bg-cyan-100 text-cyan-700' },
  qualifelec: { label: 'Qualifelec', description: 'Électricité', color: 'bg-yellow-100 text-yellow-700' },
  eco_artisan: { label: 'Éco Artisan', description: 'Engagement environnemental', color: 'bg-emerald-100 text-emerald-700' },
  handibat: { label: 'Handibat', description: 'Accessibilité', color: 'bg-purple-100 text-purple-700' },
  silverbat: { label: 'Silverbat', description: 'Adaptation seniors', color: 'bg-slate-100 text-slate-700' }
};

// Jours de la semaine
const daysOfWeek = [
  { key: 'lundi', label: 'Lundi' },
  { key: 'mardi', label: 'Mardi' },
  { key: 'mercredi', label: 'Mercredi' },
  { key: 'jeudi', label: 'Jeudi' },
  { key: 'vendredi', label: 'Vendredi' },
  { key: 'samedi', label: 'Samedi' },
  { key: 'dimanche', label: 'Dimanche' }
];

// Couleurs de marque prédéfinies
const brandColors = [
  '#1E3A5F', '#2563EB', '#059669', '#DC2626', '#7C3AED',
  '#EA580C', '#0891B2', '#4F46E5', '#BE185D', '#15803D'
];

export default function BoutiqueProPage() {
  const { user } = useOutletContext() || {};
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [boutique, setBoutique] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [metiers, setMetiers] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [typesVente, setTypesVente] = useState([]);
  const [activeTab, setActiveTab] = useState('artisan'); // artisan ou fournisseur
  
  // Form data pour chaque étape
  const [step1Data, setStep1Data] = useState({
    name: '',
    slogan: '',
    metier_principal: '',
    metier_slug: '',
    siret: '',
    type_activite: 'artisan', // artisan ou fournisseur
    type_vente: '' // ecommerce, magasin, click_collect, grossiste
  });
  
  const [step2Data, setStep2Data] = useState({
    logo_url: '',
    cover_photos: [],
    brand_color: '#1E3A5F'
  });
  
  const [step3Data, setStep3Data] = useState({
    city: '',
    postal_code: '',
    address: '',
    phone: '',
    email_pro: '',
    website: '',
    opening_hours: daysOfWeek.map(day => ({
      day: day.key,
      is_open: day.key !== 'dimanche',
      morning_open: '08:00',
      morning_close: '12:00',
      afternoon_open: '14:00',
      afternoon_close: '18:00',
      is_continuous: false
    })),
    zone_intervention_km: 50,
    description: '',
    certifications: []
  });
  
  const [step4Data, setStep4Data] = useState({
    facebook: '',
    instagram: '',
    linkedin: '',
    youtube: '',
    tiktok: ''
  });

  // URL Import state
  const [importUrl, setImportUrl] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  // Step 5: Documents Légaux
  const [step5Data, setStep5Data] = useState({
    cgv: '',
    mentions_legales: '',
    confidentialite: '',
    frais_livraison: '',
    retours: ''
  });

  useEffect(() => {
    fetchBoutique();
    fetchCategories();
  }, []);

  const fetchBoutique = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/cockpit/boutique`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.boutique) {
          setBoutique(data.boutique);
          populateFormFromBoutique(data.boutique);
          setCurrentStep(data.boutique.wizard_step || 1);
        }
      }
    } catch (error) {
      console.error('Error fetching boutique:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/cockpit/categories`);
      if (response.ok) {
        const data = await response.json();
        setMetiers(data.artisans || []);
        setFournisseurs(data.fournisseurs || []);
        setTypesVente(data.types_vente || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback to metiers only
      fetchMetiersFallback();
    }
  };

  const fetchMetiersFallback = async () => {
    try {
      const response = await fetch(`${API_URL}/api/cockpit/metiers`);
      if (response.ok) {
        setMetiers(await response.json());
      }
    } catch (error) {
      console.error('Error fetching metiers:', error);
    }
  };

  const populateFormFromBoutique = (b) => {
    const typeActivite = b.type_activite || 'artisan';
    setActiveTab(typeActivite);
    
    setStep1Data({
      name: b.name || '',
      slogan: b.slogan || '',
      metier_principal: b.metier_principal || '',
      metier_slug: b.metier_slug || '',
      siret: b.siret || '',
      type_activite: typeActivite,
      type_vente: b.type_vente || ''
    });
    
    setStep2Data({
      logo_url: b.logo_url || '',
      cover_photos: b.cover_photos || [],
      brand_color: b.brand_color || '#1E3A5F'
    });
    
    setStep3Data({
      city: b.city || '',
      postal_code: b.postal_code || '',
      address: b.address || '',
      phone: b.phone || '',
      email_pro: b.email_pro || '',
      website: b.website || '',
      opening_hours: b.opening_hours?.length ? b.opening_hours : daysOfWeek.map(day => ({
        day: day.key,
        is_open: day.key !== 'dimanche',
        morning_open: '08:00',
        morning_close: '12:00',
        afternoon_open: '14:00',
        afternoon_close: '18:00',
        is_continuous: false
      })),
      zone_intervention_km: b.zone_intervention_km || 50,
      description: b.description || '',
      certifications: b.certifications || []
    });
    
    setStep4Data({
      facebook: b.social_links?.facebook || '',
      instagram: b.social_links?.instagram || '',
      linkedin: b.social_links?.linkedin || '',
      youtube: b.social_links?.youtube || '',
      tiktok: b.social_links?.tiktok || ''
    });

    const legalDocs = b.legal_documents || {};
    setStep5Data({
      cgv: legalDocs.cgv || '',
      mentions_legales: legalDocs.mentions_legales || '',
      confidentialite: legalDocs.confidentialite || '',
      frais_livraison: legalDocs.frais_livraison || '',
      retours: legalDocs.retours || ''
    });
  };

  const saveStep = async (stepNum) => {
    setSaving(true);
    const token = localStorage.getItem('client_token');

    try {
      // Step 5: Documents légaux use a different endpoint
      if (stepNum === 5) {
        // Rien à enregistrer si tous les champs sont vides
        const tousVides = Object.values(step5Data).every((v) => !v || !v.trim());
        if (tousVides) return true;

        const response = await fetch(`${API_URL}/api/cockpit/boutique/documents`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            documents: step5Data,
            vendeur_type: step1Data.type_activite || 'artisan'
          })
        });
        if (response.ok) {
          toast.success('Documents légaux enregistrés');
          return true;
        } else if (response.status === 404) {
          // Endpoint pas encore déployé sur le backend Railway (lignée
          // production du 5 juin) : on ne bloque pas la publication
          toast.warning('Documents légaux : enregistrement bientôt disponible — la publication continue');
          return true;
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Erreur lors de l\'enregistrement');
          return false;
        }
      }

      let payload;
      switch (stepNum) {
        // type_activite / type_vente restent locaux : le backend (extra=forbid
        // sur Railway) rejette en 422 tout champ inconnu
        case 1: payload = {
          name: step1Data.name,
          slogan: step1Data.slogan,
          metier_principal: step1Data.metier_principal,
          metier_slug: step1Data.metier_slug,
          siret: step1Data.siret
        }; break;
        case 2: payload = step2Data; break;
        case 3: payload = step3Data; break;
        case 4: payload = step4Data; break;
        default: return;
      }

      const response = await fetch(`${API_URL}/api/cockpit/boutique/step${stepNum}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setBoutique(data.boutique);
        toast.success(`Étape ${stepNum} enregistrée`);
        if (stepNum < 5) {
          setCurrentStep(stepNum + 1);
        }
        return true;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'enregistrement');
        return false;
      }
    } catch (error) {
      console.error('Error saving step:', error);
      toast.error('Erreur lors de l\'enregistrement');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const publishBoutique = async () => {
    // D'abord sauvegarder les documents légaux (étape 5)
    const saved = await saveStep(5);
    if (!saved) return;

    setSaving(true);
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/cockpit/boutique/publish`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setBoutique(data.boutique);
        toast.success('Boutique publiée ! 🎉');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la publication');
      }
    } catch (error) {
      toast.error('Erreur lors de la publication');
    } finally {
      setSaving(false);
    }
  };

  const handleMetierSelect = (metier) => {
    setStep1Data(prev => ({
      ...prev,
      metier_principal: metier.name,
      metier_slug: metier.slug
    }));
  };

  const toggleCertification = (cert) => {
    setStep3Data(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };

  const updateOpeningHours = (dayKey, field, value) => {
    setStep3Data(prev => ({
      ...prev,
      opening_hours: prev.opening_hours.map(day =>
        day.day === dayKey ? { ...day, [field]: value } : day
      )
    }));
  };

  const handleUrlImport = async () => {
    if (!importUrl) return;
    setIsImporting(true);
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/cockpit/boutique/import-from-url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url: importUrl })
      });
      if (response.ok) {
        const data = await response.json();
        const ex = data.extracted || {};
        if (ex.name || ex.slogan) {
          setStep1Data(prev => ({
            ...prev,
            name: ex.name || prev.name,
            slogan: ex.slogan || prev.slogan
          }));
        }
        setStep3Data(prev => ({
          ...prev,
          phone: ex.phone || prev.phone,
          email_pro: ex.email || prev.email_pro,
          city: ex.city || prev.city,
          address: ex.address || prev.address,
          website: importUrl,
          description: ex.description || prev.description
        }));
        toast.success('Informations importées depuis votre site !');
      } else if (response.status === 404) {
        // Endpoint pas encore déployé sur le backend Railway
        toast.warning('Analyse de site bientôt disponible — remplissez les champs à la main pour l\'instant');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Impossible d\'analyser ce site');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'analyse du site');
    } finally {
      setIsImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  // Steps configuration
  const steps = [
    { id: 1, label: 'Identité', icon: User, description: 'Nom, métier, SIRET' },
    { id: 2, label: 'Visuels', icon: ImageIcon, description: 'Logo, photos, couleur' },
    { id: 3, label: 'Informations', icon: MapPin, description: 'Adresse, horaires' },
    { id: 4, label: 'Réseaux', icon: Share2, description: 'Facebook, Instagram...' },
    { id: 5, label: 'Documents', icon: Shield, description: 'CGV, mentions légales' }
  ];

  const isStepCompleted = (stepNum) => {
    return boutique?.wizard_step > stepNum;
  };

  const canAccessStep = (stepNum) => {
    if (stepNum === 1) return true;
    return boutique?.wizard_step >= stepNum - 1;
  };

  return (
    <div className="space-y-6" data-testid="boutique-pro-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-rm-text">Ma Boutique Pro</h1>
          <p className="text-rm-text-secondary">
            Créez votre vitrine professionnelle en 5 étapes
          </p>
        </div>
        {boutique?.status === 'active' && boutique?.slug && (
          <a
            href={`/boutique/${boutique.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-rm-primary text-white rounded-lg hover:bg-rm-primary/90 transition-colors"
          >
            <Eye className="w-4 h-4" />
            Voir ma boutique
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>

      {/* Status Banner */}
      {boutique && (
        <div className={`rounded-xl p-4 flex items-center gap-4 ${
          boutique.status === 'active' 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-yellow-50 border border-yellow-200'
        }`}>
          {boutique.status === 'active' ? (
            <>
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div className="flex-1">
                <p className="font-medium text-green-800">Boutique publiée</p>
                <p className="text-sm text-green-700">
                  Visible sur <span className="font-medium">/boutique/{boutique.slug}</span>
                </p>
              </div>
            </>
          ) : (
            <>
              <AlertCircle className="w-6 h-6 text-yellow-600" />
              <div className="flex-1">
                <p className="font-medium text-yellow-800">Boutique en brouillon</p>
                <p className="text-sm text-yellow-700">
                  Complétez les 5 étapes pour publier votre vitrine
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Steps Progress */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = isStepCompleted(step.id);
            const canAccess = canAccessStep(step.id);
            
            return (
              <div key={step.id} className="flex items-center">
                <button
                  onClick={() => canAccess && setCurrentStep(step.id)}
                  disabled={!canAccess}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all ${
                    isActive ? 'bg-rm-primary text-white shadow-lg scale-105' :
                    isCompleted ? 'bg-green-100 text-green-700 hover:bg-green-200' :
                    canAccess ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' :
                    'bg-slate-50 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isActive ? 'bg-white/20' :
                    isCompleted ? 'bg-green-200' : 'bg-slate-200'
                  }`}>
                    {isCompleted ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium">{step.label}</p>
                    <p className={`text-xs ${isActive ? 'text-white/80' : 'text-slate-500'} hidden sm:block`}>
                      {step.description}
                    </p>
                  </div>
                </button>
                
                {index < steps.length - 1 && (
                  <div className={`w-8 md:w-16 h-1 mx-2 rounded ${
                    isCompleted ? 'bg-green-400' : 'bg-slate-200'
                  }`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
        <AnimatePresence mode="wait">
          {/* Step 1: Identité */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
              data-testid="step-1-identity"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-rm-primary/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-rm-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-rm-text">Étape 1 : Identité</h2>
                  <p className="text-rm-text-secondary">Présentez votre entreprise</p>
                </div>
              </div>

              {/* ─── Import depuis un site existant ─── */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Link2 className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-900">Vous avez déjà un site web ?</span>
                  <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">Auto-remplissage ✨</span>
                </div>
                <p className="text-sm text-blue-700 mb-3">
                  Entrez l'adresse de votre site et on importe automatiquement vos informations.
                </p>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="url"
                      value={importUrl}
                      onChange={(e) => setImportUrl(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 bg-white border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm"
                      placeholder="https://www.monsiteweb.fr"
                      onKeyDown={(e) => e.key === 'Enter' && handleUrlImport()}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleUrlImport}
                    disabled={isImporting || !importUrl}
                    className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium whitespace-nowrap"
                  >
                    {isImporting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Analyse...
                      </>
                    ) : (
                      <>
                        <Link2 className="w-4 h-4" />
                        Analyser
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-rm-text mb-1">
                    Nom de votre boutique <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    maxLength={100}
                    value={step1Data.name}
                    onChange={(e) => setStep1Data({ ...step1Data, name: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20 text-lg"
                    placeholder="Ex: Plomberie Martin & Fils"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-rm-text mb-1">
                    Slogan / Accroche
                    <span className="ml-2 text-xs text-slate-400">({step1Data.slogan.length}/80)</span>
                  </label>
                  <input
                    type="text"
                    maxLength={80}
                    value={step1Data.slogan}
                    onChange={(e) => setStep1Data({ ...step1Data, slogan: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    placeholder="Ex: Votre plombier de confiance depuis 1995"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-rm-text mb-3">
                    Type d'activité <span className="text-red-500">*</span>
                  </label>
                  
                  {/* Onglets Artisan / Fournisseur */}
                  <div className="flex gap-2 mb-4 p-1 bg-slate-100 rounded-xl">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab('artisan');
                        setStep1Data({ ...step1Data, type_activite: 'artisan', metier_slug: '', metier_principal: '', type_vente: '' });
                      }}
                      className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                        activeTab === 'artisan'
                          ? 'bg-white text-[#1E3A5F] shadow-sm'
                          : 'text-slate-600 hover:text-slate-800'
                      }`}
                    >
                      <span className="text-xl">🔧</span>
                      Artisan / Prestataire
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab('fournisseur');
                        setStep1Data({ ...step1Data, type_activite: 'fournisseur', metier_slug: '', metier_principal: '' });
                      }}
                      className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                        activeTab === 'fournisseur'
                          ? 'bg-white text-[#F97316] shadow-sm'
                          : 'text-slate-600 hover:text-slate-800'
                      }`}
                    >
                      <span className="text-xl">🏪</span>
                      Fournisseur / Vendeur
                    </button>
                  </div>

                  {/* Grille de métiers - Artisans */}
                  {activeTab === 'artisan' && (
                    <>
                      <p className="text-sm text-slate-500 mb-3">Sélectionne ton métier principal (16 métiers disponibles)</p>
                      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                        {metiers.map((metier) => (
                          <button
                            key={metier.slug}
                            type="button"
                            onClick={() => handleMetierSelect(metier)}
                            className={`p-2 rounded-lg border-2 text-center transition-all ${
                              step1Data.metier_slug === metier.slug
                                ? 'border-[#1E3A5F] bg-[#1E3A5F]/5 shadow-sm'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <span className="text-xl block">{metier.icon}</span>
                            <span className="text-xs font-medium text-rm-text line-clamp-1">{metier.name}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Grille de catégories - Fournisseurs */}
                  {activeTab === 'fournisseur' && (
                    <>
                      <p className="text-sm text-slate-500 mb-3">Sélectionne ta catégorie de produits (13 catégories disponibles)</p>
                      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                        {fournisseurs.map((cat) => (
                          <button
                            key={cat.slug}
                            type="button"
                            onClick={() => handleMetierSelect(cat)}
                            className={`p-3 rounded-xl border-2 text-center transition-all ${
                              step1Data.metier_slug === cat.slug
                                ? 'border-[#F97316] bg-[#F97316]/5 shadow-sm'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <span className="text-2xl block mb-1">{cat.icon}</span>
                            <span className="text-xs font-medium text-rm-text line-clamp-2">{cat.name}</span>
                          </button>
                        ))}
                      </div>

                      {/* Type de vente pour les fournisseurs */}
                      {step1Data.metier_slug && (
                        <div className="mt-6 p-4 bg-orange-50 rounded-xl border border-orange-100">
                          <label className="block text-sm font-medium text-slate-700 mb-3">
                            Type de point de vente <span className="text-red-500">*</span>
                          </label>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {typesVente.map((type) => (
                              <button
                                key={type.id}
                                type="button"
                                onClick={() => setStep1Data({ ...step1Data, type_vente: type.id })}
                                className={`p-3 rounded-lg border-2 text-center transition-all ${
                                  step1Data.type_vente === type.id
                                    ? 'border-[#F97316] bg-white shadow-sm'
                                    : 'border-slate-200 bg-white hover:border-slate-300'
                                }`}
                              >
                                <span className="text-sm font-medium text-slate-800">{type.label}</span>
                                <span className="text-xs text-slate-500 block mt-1">{type.description}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">
                    N° SIRET
                    <span className="ml-2 text-xs text-slate-400">(optionnel, pour badge vérifié)</span>
                  </label>
                  <input
                    type="text"
                    maxLength={14}
                    value={step1Data.siret}
                    onChange={(e) => setStep1Data({ ...step1Data, siret: e.target.value.replace(/\D/g, '') })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    placeholder="12345678901234"
                  />
                  {step1Data.siret?.length === 14 && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Badge "Entreprise vérifiée" activé
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Visuels */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
              data-testid="step-2-visuals"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-rm-primary/10 flex items-center justify-center">
                  <ImageIcon className="w-6 h-6 text-rm-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-rm-text">Étape 2 : Visuels</h2>
                  <p className="text-rm-text-secondary">Logo, photos de couverture et couleur de marque</p>
                </div>
              </div>

              {/* Logo */}
              <div>
                <label className="block text-sm font-medium text-rm-text mb-3">Logo</label>
                <div className="flex items-start gap-6">
                  <div className="w-32 h-32 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50 overflow-hidden">
                    {step2Data.logo_url ? (
                      <img src={step2Data.logo_url} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <Store className="w-12 h-12 text-slate-300" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-rm-text-secondary mb-3">
                      Recommandé : 500x500px minimum, format carré
                    </p>
                    <ImageUploader
                      images={step2Data.logo_url ? [{ url: step2Data.logo_url, id: 'logo' }] : []}
                      onChange={(imgs) => setStep2Data({ ...step2Data, logo_url: imgs[0]?.url || '' })}
                      maxImages={1}
                      folder="logos"
                    />
                  </div>
                </div>
              </div>

              {/* Photos de couverture */}
              <div>
                <label className="block text-sm font-medium text-rm-text mb-3">
                  Photos de couverture
                  <span className="ml-2 text-xs text-slate-400">(max 3)</span>
                </label>
                <ImageUploader
                  images={step2Data.cover_photos.map((url, i) => ({ url, id: `cover-${i}` }))}
                  onChange={(imgs) => setStep2Data({ ...step2Data, cover_photos: imgs.map(i => i.url) })}
                  maxImages={3}
                  folder="covers"
                />
              </div>

              {/* Couleur de marque */}
              <div>
                <label className="block text-sm font-medium text-rm-text mb-3">Couleur de marque</label>
                <div className="flex items-center gap-4">
                  <div className="flex gap-2">
                    {brandColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setStep2Data({ ...step2Data, brand_color: color })}
                        className={`w-10 h-10 rounded-lg transition-transform ${
                          step2Data.brand_color === color ? 'scale-110 ring-2 ring-offset-2 ring-rm-primary' : ''
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={step2Data.brand_color}
                      onChange={(e) => setStep2Data({ ...step2Data, brand_color: e.target.value })}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <span className="text-sm text-slate-500">{step2Data.brand_color}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Informations */}
          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
              data-testid="step-3-info"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-rm-primary/10 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-rm-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-rm-text">Étape 3 : Informations</h2>
                  <p className="text-rm-text-secondary">Coordonnées, horaires et certifications</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">
                    Ville <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={step3Data.city}
                    onChange={(e) => setStep3Data({ ...step3Data, city: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    placeholder="Paris"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">
                    Code postal <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    maxLength={5}
                    value={step3Data.postal_code}
                    onChange={(e) => setStep3Data({ ...step3Data, postal_code: e.target.value.replace(/\D/g, '') })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    placeholder="75001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Zone d'intervention</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min="5"
                      max="200"
                      step="5"
                      value={step3Data.zone_intervention_km}
                      onChange={(e) => setStep3Data({ ...step3Data, zone_intervention_km: parseInt(e.target.value) })}
                      className="flex-1"
                    />
                    <span className="text-sm font-medium w-16 text-right">{step3Data.zone_intervention_km} km</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-rm-text mb-1">Adresse complète</label>
                <input
                  type="text"
                  value={step3Data.address}
                  onChange={(e) => setStep3Data({ ...step3Data, address: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  placeholder="123 rue de la Plomberie"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Téléphone</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="tel"
                      value={step3Data.phone}
                      onChange={(e) => setStep3Data({ ...step3Data, phone: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      placeholder="01 23 45 67 89"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Email professionnel</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="email"
                      value={step3Data.email_pro}
                      onChange={(e) => setStep3Data({ ...step3Data, email_pro: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      placeholder="contact@exemple.fr"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Site web</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="url"
                      value={step3Data.website}
                      onChange={(e) => setStep3Data({ ...step3Data, website: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-rm-text mb-1">
                  Description de votre activité <span className="text-red-500">*</span>
                  <span className="ml-2 text-xs text-slate-400">({step3Data.description.length}/2000)</span>
                </label>
                <textarea
                  maxLength={2000}
                  value={step3Data.description}
                  onChange={(e) => setStep3Data({ ...step3Data, description: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  rows={4}
                  placeholder="Présentez votre entreprise, vos services, votre expérience..."
                />
              </div>

              {/* Horaires */}
              <div>
                <label className="block text-sm font-medium text-rm-text mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Horaires d'ouverture
                </label>
                <div className="space-y-2">
                  {step3Data.opening_hours.map((day) => {
                    const dayLabel = daysOfWeek.find(d => d.key === day.day)?.label || day.day;
                    return (
                      <div key={day.day} className="flex items-center gap-4 py-2 border-b border-slate-100 last:border-0">
                        <div className="w-24">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={day.is_open}
                              onChange={(e) => updateOpeningHours(day.day, 'is_open', e.target.checked)}
                              className="w-4 h-4 rounded border-slate-300 text-rm-primary"
                            />
                            <span className={`text-sm font-medium ${day.is_open ? 'text-rm-text' : 'text-slate-400'}`}>
                              {dayLabel}
                            </span>
                          </label>
                        </div>
                        {day.is_open && (
                          <>
                            <div className="flex items-center gap-2">
                              <input
                                type="time"
                                value={day.morning_open}
                                onChange={(e) => updateOpeningHours(day.day, 'morning_open', e.target.value)}
                                className="px-2 py-1 border border-slate-200 rounded text-sm"
                              />
                              <span className="text-slate-400">-</span>
                              <input
                                type="time"
                                value={day.morning_close}
                                onChange={(e) => updateOpeningHours(day.day, 'morning_close', e.target.value)}
                                className="px-2 py-1 border border-slate-200 rounded text-sm"
                              />
                            </div>
                            {!day.is_continuous && (
                              <div className="flex items-center gap-2">
                                <input
                                  type="time"
                                  value={day.afternoon_open}
                                  onChange={(e) => updateOpeningHours(day.day, 'afternoon_open', e.target.value)}
                                  className="px-2 py-1 border border-slate-200 rounded text-sm"
                                />
                                <span className="text-slate-400">-</span>
                                <input
                                  type="time"
                                  value={day.afternoon_close}
                                  onChange={(e) => updateOpeningHours(day.day, 'afternoon_close', e.target.value)}
                                  className="px-2 py-1 border border-slate-200 rounded text-sm"
                                />
                              </div>
                            )}
                            <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-500">
                              <input
                                type="checkbox"
                                checked={day.is_continuous}
                                onChange={(e) => updateOpeningHours(day.day, 'is_continuous', e.target.checked)}
                                className="w-3 h-3 rounded"
                              />
                              Continu
                            </label>
                          </>
                        )}
                        {!day.is_open && (
                          <span className="text-sm text-slate-400">Fermé</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Certifications */}
              <div>
                <label className="block text-sm font-medium text-rm-text mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Certifications & Labels
                </label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(certificationLabels).map(([key, { label, description, color }]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleCertification(key)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                        step3Data.certifications.includes(key)
                          ? `${color} border-2 border-current`
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      title={description}
                    >
                      {step3Data.certifications.includes(key) && <Check className="w-4 h-4" />}
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 4: Réseaux Sociaux */}
          {currentStep === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
              data-testid="step-4-social"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-rm-primary/10 flex items-center justify-center">
                  <Share2 className="w-6 h-6 text-rm-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-rm-text">Étape 4 : Réseaux Sociaux</h2>
                  <p className="text-rm-text-secondary">Connectez vos comptes pour plus de visibilité</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1 flex items-center gap-2">
                    <Facebook className="w-4 h-4 text-blue-600" />
                    Facebook
                  </label>
                  <input
                    type="url"
                    value={step4Data.facebook}
                    onChange={(e) => setStep4Data({ ...step4Data, facebook: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    placeholder="https://facebook.com/..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1 flex items-center gap-2">
                    <Instagram className="w-4 h-4 text-pink-600" />
                    Instagram
                  </label>
                  <input
                    type="url"
                    value={step4Data.instagram}
                    onChange={(e) => setStep4Data({ ...step4Data, instagram: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    placeholder="https://instagram.com/..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1 flex items-center gap-2">
                    <Linkedin className="w-4 h-4 text-blue-700" />
                    LinkedIn
                  </label>
                  <input
                    type="url"
                    value={step4Data.linkedin}
                    onChange={(e) => setStep4Data({ ...step4Data, linkedin: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    placeholder="https://linkedin.com/company/..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1 flex items-center gap-2">
                    <Youtube className="w-4 h-4 text-red-600" />
                    YouTube
                  </label>
                  <input
                    type="url"
                    value={step4Data.youtube}
                    onChange={(e) => setStep4Data({ ...step4Data, youtube: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    placeholder="https://youtube.com/@..."
                  />
                </div>
              </div>

              {/* Preview */}
              {boutique && (
                <div className="border-t border-slate-200 pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-rm-text mb-4">Récapitulatif</h3>
                  <div className="bg-slate-50 rounded-xl p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Nom</span>
                      <p className="font-medium text-rm-text">{step1Data.name || '-'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Métier</span>
                      <p className="font-medium text-rm-text">{step1Data.metier_principal || '-'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Ville</span>
                      <p className="font-medium text-rm-text">{step3Data.city || '-'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Zone</span>
                      <p className="font-medium text-rm-text">{step3Data.zone_intervention_km} km</p>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 5: Documents Légaux */}
          {currentStep === 5 && (
            <motion.div
              key="step5"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
              data-testid="step-5-documents"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-rm-primary/10 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-rm-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-rm-text">Étape 5 : Documents Légaux</h2>
                  <p className="text-rm-text-secondary">CGV, mentions légales et politiques de votre boutique</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-800">
                  <strong>Ces documents sont obligatoires</strong> pour toute boutique en ligne en France.
                  Vous pouvez les compléter maintenant ou plus tard depuis{' '}
                  <strong>Paramètres → Documents Légaux</strong>.
                </p>
              </div>

              {/* CGV */}
              <div>
                <label className="block text-sm font-medium text-rm-text mb-1 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  Conditions Générales de Vente (CGV)
                  <span className="text-xs text-red-400 font-normal">obligatoire</span>
                </label>
                <textarea
                  value={step5Data.cgv}
                  onChange={(e) => setStep5Data(prev => ({ ...prev, cgv: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20 font-mono text-sm"
                  rows={6}
                  placeholder="Vos conditions générales de vente : modalités de commande, paiement, livraison, rétractation..."
                />
              </div>

              {/* Mentions légales */}
              <div>
                <label className="block text-sm font-medium text-rm-text mb-1 flex items-center gap-2">
                  <Scale className="w-4 h-4 text-purple-600" />
                  Mentions Légales
                  <span className="text-xs text-red-400 font-normal">obligatoire</span>
                </label>
                <textarea
                  value={step5Data.mentions_legales}
                  onChange={(e) => setStep5Data(prev => ({ ...prev, mentions_legales: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20 font-mono text-sm"
                  rows={5}
                  placeholder="Éditeur du site, SIRET, directeur de publication, hébergeur..."
                />
              </div>

              {/* Confidentialité */}
              <div>
                <label className="block text-sm font-medium text-rm-text mb-1 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  Politique de Confidentialité
                </label>
                <textarea
                  value={step5Data.confidentialite}
                  onChange={(e) => setStep5Data(prev => ({ ...prev, confidentialite: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20 font-mono text-sm"
                  rows={5}
                  placeholder="Collecte et traitement des données personnelles, cookies, droits RGPD..."
                />
              </div>

              {/* Documents produit (fournisseurs) */}
              {step1Data.type_activite === 'fournisseur' && (
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-base font-semibold text-rm-text mb-4 flex items-center gap-2">
                    <Package className="w-4 h-4 text-orange-600" />
                    Documents spécifiques à la vente de produits
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-rm-text mb-1">
                        Frais de livraison
                      </label>
                      <textarea
                        value={step5Data.frais_livraison}
                        onChange={(e) => setStep5Data(prev => ({ ...prev, frais_livraison: e.target.value }))}
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20 font-mono text-sm"
                        rows={4}
                        placeholder="Tarifs, zones et délais de livraison..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-rm-text mb-1">
                        Retours & Remboursements
                      </label>
                      <textarea
                        value={step5Data.retours}
                        onChange={(e) => setStep5Data(prev => ({ ...prev, retours: e.target.value }))}
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20 font-mono text-sm"
                        rows={4}
                        placeholder="Conditions de retour, délais de remboursement..."
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-sm text-slate-600">
                  💡 Vous pouvez aussi uploader des PDFs depuis{' '}
                  <strong>Cockpit Pro → Paramètres → Documents Légaux</strong>.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
          <div>
            {currentStep > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setCurrentStep(currentStep - 1)}
              >
                Précédent
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            {currentStep < 5 ? (
              <Button
                type="button"
                onClick={() => saveStep(currentStep)}
                disabled={saving}
                className="bg-rm-primary"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    Suivant
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => saveStep(5)}
                  disabled={saving}
                >
                  Enregistrer le brouillon
                </Button>
                <Button
                  type="button"
                  onClick={publishBoutique}
                  disabled={saving || !step1Data.name || !step3Data.city || !step3Data.description}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Publication...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Publier ma boutique
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
